import { StringData } from "../../core/StringData";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";
import { CursorReactiveModule } from "./CursorReactiveModule";

type VelocityState = {
  smoothVx: number;
  smoothVy: number;
  prevSpeed: number;
  prevVx: number;
  prevVy: number;
  prevAngle: number;
};

const velocityState = new WeakMap<StringObject, VelocityState>();

const getState = (object: StringObject): VelocityState => {
  let state = velocityState.get(object);
  if (!state) {
    state = {
      smoothVx: 0,
      smoothVy: 0,
      prevSpeed: NaN,
      prevVx: NaN,
      prevVy: NaN,
      prevAngle: NaN,
    };
    velocityState.set(object, state);
  }
  return state;
};

const round3 = (value: number) => Math.round(value * 1000) / 1000;

export class StringVelocity extends CursorReactiveModule {
  private lastFrameTime = 0;

  constructor(context: any) {
    super(context);
    this.htmlKey = "velocity";
    this._type = 2;

    this.cssProperties = [
      { name: "--cursor-speed", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--cursor-vx", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--cursor-vy", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--cursor-angle", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.nearOnly = false;
    this.useAllObjects = true;

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "velocity-smoothing",
        type: "number",
        fallback: this.settings["velocity-smoothing"] ?? 0.12,
      },
      {
        key: "velocity-max",
        type: "number",
        fallback: this.settings["velocity-max"] ?? 3,
      },
      {
        key: "velocity-threshold",
        type: "number",
        fallback: this.settings["velocity-threshold"] ?? 0.005,
      },
    ];
  }

  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>,
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("__needs-velocity-update", true);
    object.setProperty("__next-cursor-speed", 0);
    object.setProperty("__next-cursor-vx", 0);
    object.setProperty("__next-cursor-vy", 0);
    object.setProperty("__next-cursor-angle", 0);
    object.setProperty("event-velocity-name", this.getObjectEventName(object, "cursor:velocity"));
  }

  override onMutate(data: StringData): void {
    if (!this.enabled || this.objectsOnPage.length === 0) return;
    super.onMutate(data);

    const now = performance.now();
    let dt = this.lastFrameTime ? (now - this.lastFrameTime) / 1000 : 1 / 60;
    this.lastFrameTime = now;
    if (dt > 0.1) dt = 0.1;
    if (dt < 1 / 240) dt = 1 / 240;

    const rawVx = this.data.cursor.velocityX * 1000;
    const rawVy = this.data.cursor.velocityY * 1000;
    const frames = dt * 60;

    let firstPayload:
      | {
          speed: number;
          vx: number;
          vy: number;
          angle: number;
        }
      | null = null;

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const object = this.objectsOnPage[i];
      const state = getState(object);
      const smoothing = object.getProperty<number>("velocity-smoothing") ?? 0.12;
      const velocityMax = object.getProperty<number>("velocity-max") ?? 3;
      const threshold = object.getProperty<number>("velocity-threshold") ?? 0.005;
      const factor = 1 - Math.pow(1 - Math.min(0.99, smoothing), frames);

      state.smoothVx += (rawVx - state.smoothVx) * factor;
      state.smoothVy += (rawVy - state.smoothVy) * factor;

      const maxPxPerSec = Math.max(0.001, velocityMax) * 1000;
      const normVx = Math.max(-1, Math.min(1, state.smoothVx / maxPxPerSec));
      const normVy = Math.max(-1, Math.min(1, state.smoothVy / maxPxPerSec));
      const speed = Math.min(1, Math.hypot(normVx, normVy));
      const angle = normVx === 0 && normVy === 0 ? 0 : Math.atan2(normVy, normVx);

      const cssSpeed = round3(speed);
      const cssVx = round3(normVx);
      const cssVy = round3(normVy);
      const cssAngle = round3(angle);

      const changed =
        Number.isNaN(state.prevSpeed) ||
        Number.isNaN(state.prevVx) ||
        Number.isNaN(state.prevVy) ||
        Number.isNaN(state.prevAngle) ||
        Math.abs(cssSpeed - state.prevSpeed) > threshold ||
        Math.abs(cssVx - state.prevVx) > threshold ||
        Math.abs(cssVy - state.prevVy) > threshold ||
        Math.abs(cssAngle - state.prevAngle) > threshold;

      if (i === 0 && firstPayload == null) {
        firstPayload = {
          speed: cssSpeed,
          vx: cssVx,
          vy: cssVy,
          angle: cssAngle,
        };
      }

      object.setProperty("__needs-velocity-update", changed);
      if (!changed) {
        continue;
      }

      state.prevSpeed = cssSpeed;
      state.prevVx = cssVx;
      state.prevVy = cssVy;
      state.prevAngle = cssAngle;

      object.setProperty("__next-cursor-speed", cssSpeed);
      object.setProperty("__next-cursor-vx", cssVx);
      object.setProperty("__next-cursor-vy", cssVy);
      object.setProperty("__next-cursor-angle", cssAngle);
    }

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const object = this.objectsOnPage[i];
      const needsUpdate = object.getProperty<boolean>("__needs-velocity-update") === true;
      if (!needsUpdate) continue;

      const speed = object.getProperty<number>("__next-cursor-speed") ?? 0;
      const vx = object.getProperty<number>("__next-cursor-vx") ?? 0;
      const vy = object.getProperty<number>("__next-cursor-vy") ?? 0;
      const angle = object.getProperty<number>("__next-cursor-angle") ?? 0;

      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVar(el, "--cursor-speed", speed);
        styleTxn.setVar(el, "--cursor-vx", vx);
        styleTxn.setVar(el, "--cursor-vy", vy);
        styleTxn.setVar(el, "--cursor-angle", angle);
      });

      const eventName = object.getProperty<string>("event-velocity-name");
      if (eventName) {
        this.events.emit(eventName, { speed, vx, vy, angle });
      }

      object.setProperty("__needs-velocity-update", false);
    }

    if (firstPayload) {
      this.events.emit("cursor:velocity", firstPayload);
    }
  }
}
